import { IFieldElem } from '@models/IFormData';
import { IConditionalVariable, IDictVariable, IVariable, IVariables } from '@models/Forms/IForms';
import { resolveFormVariable } from './resolveFormVariable';
import { DictionariesService } from '@services/DictionariesService';
import { IUser } from '@models/IUser';
import { evaluateConditionalVariables } from './conditionalVariables';
import { createVariablesFormula } from './createVariablesFormula';

export class VariablesManager {
    private _variables: IVariables;
    private _docId: string;
    private _currentUser: IUser;
    private _resolveDictionaryVariables: boolean;

    constructor(
        variables: IVariables,
        docId: string,
        currentUser: IUser,
        options: { resolveDictionaryVariables?: boolean } = {},
    ) {
        this._variables = variables;
        this._docId = docId;
        this._currentUser = currentUser;
        this._resolveDictionaryVariables = options.resolveDictionaryVariables ?? true;
    }

    async GetData(data: Record<string, IFieldElem>, isEdit: boolean, isNew: boolean) {
        const result: Record<string, any> = Object.create(null);

        const allIds = new Set([
            this._currentUser.id.toString(),
            ...(this._currentUser.assistantKeys || '').split(',').filter((id) => id),
            ...(this._currentUser.dummies || '').split(',').filter((id) => id),
        ]);
        const uniqueIdsArray = Array.from(allIds);

        result['docId'] = this._docId;

        result['currentUserId'] = this._currentUser.id;
        result['currentUserAssistantIds'] = uniqueIdsArray.join(',');
        result['currentUser'] = this._currentUser.name;
        result['currentUserFullName'] = this._currentUser.fullName;
        result['currentUserFirstName'] = this._currentUser.firstName;
        result['currentUserSurName'] = this._currentUser.lastName;
        result['currentUserPatronymic'] = this._currentUser.middleName;
        result['currentUserMailbox'] = this._currentUser.email;
        result['currentUserPhoneNumber'] = this._currentUser.phone;
        result['currentUserRole'] = this._currentUser.role;
        result['currentUserDepartment'] = this._currentUser.department;
        result['currentUserDepartmentId'] = this._currentUser.departmentId;
        result['currentUserPost'] = this._currentUser.position;
        result['currentUserSubdivision'] = this._currentUser.subdivision;
        result['currentUserSubdivisionInn'] = this._currentUser.subdivisionInn;
        result['currentUserWorkGroups'] = this._currentUser.workGroups;
        result['currentUserAccessGroups'] = this._currentUser.accessGroups;

        const isConditional = (variable: IVariable): variable is IVariable & IConditionalVariable =>
            variable.name != null || variable.type != null || variable.if != null || !!variable.elseif?.length || variable.else != null;
        const conditionalVariables = (this._variables.variable ?? []).filter(isConditional);
        const conditionalNames = new Set(conditionalVariables.map(({ name }) => name).filter((name): name is string => !!name));
        const stringNames = new Set(conditionalVariables.filter(({ type }) => type === 'string').map(({ name }) => name));
        const declaredNames = new Set<string>();
        for (const variable of this._variables.variable ?? []) {
            const name = isConditional(variable) ? variable.name : variable.variableName;
            if (name && declaredNames.has(name) && conditionalNames.has(name)) throw new Error(`Global has an invalid or duplicate variable name "${name}".`);
            if (name) declaredNames.add(name);
        }
        for (const variable of this._variables.dictVariable ?? []) {
            if (conditionalNames.has(variable.variableName)) throw new Error(`Global has an invalid or duplicate variable name "${variable.variableName}".`);
        }
        const resolvedNames = new Set<string>();
        const variablesFormula = createVariablesFormula(data);
        for (const variable of this._variables.variable ?? []) {
            if (isConditional(variable)) {
                if (!variable.name) throw new Error('Global has an invalid or duplicate variable name "".');
                const resolved = await evaluateConditionalVariables(
                    [variable],
                    result,
                    'Global',
                    (expression, values) =>
                        variablesFormula(expression, values).EvalFormulaValues(isEdit, isNew, undefined, true),
                    { allowShadowInitialValues: true, declaredNames: conditionalNames, alreadyResolvedNames: resolvedNames, stringNames },
                );
                Object.assign(result, resolved);
                resolvedNames.add(variable.name);
                continue;
            }
            const value = resolveFormVariable(variable, data);
            if (value !== undefined && variable.variableName) result[variable.variableName] = value;
        }
        for (const variable of this._resolveDictionaryVariables ? this._variables.dictVariable ?? [] : []) {
            if (
                (variable.onlyNew && isNew) ||
                (variable.onlyEdit && isEdit) ||
                (!variable.onlyEdit && !variable.onlyNew)
            ) {
                result[variable.variableName] = await this.getDictVariable(variable, data);
            }
        }

        return result;
    }
    private async getDictVariable(variable: IDictVariable, data: Record<string, IFieldElem>) {
        let code = variable.code;
        if (code.includes('|Document')) {
            code = data[code].value;
        }

        let response = await DictionariesService.getSubTreeByCode(variable.name, code);
        return response.data;
    }
}
